import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { booleanScheme, stringEmailScheme, stringScheme } from 'src/utils/validation-schemes';
import { WorkflowsEditIcon } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsRow/WorkflowsRowIcons';
import { WorkflowNameAndDescriptionBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowNameAndDescriptionBlock';
import { WorkflowActionsBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowActionsBlock';
import { WorkflowTriggersBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowTriggersBlock';
import { getWorkflowRequestAction } from 'src/pages/ConfigurationPage/WorkflowsTab/getWorkflowRequestAction';
import { validateWorkflowActions } from 'src/helpers/validateWorkflowActions';
import { useNotification } from 'src/hooks/useNotification';

export const EditWorkflowPopup = ({
  row = {} as Record<string, any>,
  onSubmit,
  isCustom = false,
  disabled = false,
}) => {
  const [workflow, setWorkflow] = useState(row);
  const [open, setOpen] = useState(false);
  const notification = useNotification();

  useEffect(() => {
    if (row) {
      setWorkflow(row);
    }
  }, [row]);

  const { handleSubmit, control, reset, watch, setValue, trigger, setError } = useForm<any>({
    defaultValues: async () => {
      const action = Array.isArray(workflow.action)
        ? workflow.action
        : workflow.action
          ? [workflow.action]
          : [];

      return {
        name: workflow.name || '',
        description: workflow.description || '',
        trigger_type: workflow.trigger_type || 'when_asked',
        trigger_value: workflow.trigger_value || '',
        action: action.map((action) => {
          const customActionFields: Record<string, any> = {};
          if (
            action.fields &&
            Boolean(action.fields.filter((field) => field.type === 'custom').length)
          ) {
            customActionFields.action_fields_custom_array = action.fields.filter(
              (field) => field.type === 'custom',
            );
          }

          return {
            action_type:
              action.type &&
              ['collect_and_escalate', 'forward_to_email', 'handle_with_crm', 'close'].includes(
                action.type,
              )
                ? 'escalate'
                : action.type || 'clarify',
            action_fields_custom: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'custom').length,
            ),
            action_fields_name: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'name').length,
            ),
            action_fields_email: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'email').length,
            ),
            action_fields_phone_number: Boolean(
              action.fields &&
              action.fields.filter((field) => field.type === 'phone_number').length,
            ),
            action_message_type: action.message_type || 'transcript',
            message_before: action.message_before || '',
            receiver_email_addresses: action.receiver_email_addresses
              ? action.receiver_email_addresses.join(', ')
              : '',
            sender_name: action.sender_name,
            subject: action.subject,
            sender_email_address: action.sender_email_address,
            message_after: action.message_after || '',
            action_message: action.message || '',
            action_escalated_list: action.type || '',
            action_data_collection: '',
            message_close: action.message || '',
            ...customActionFields,
          };
        }),
      };
    },
    resolver: yupResolver(
      yup.object({
        name: stringScheme({ required: true }),
        description: stringScheme({ required: false }),
        trigger_type: stringScheme({ required: false }),
        trigger_value: stringScheme({ required: isCustom }),
        action: yup.array(
          yup.object({
            action_type: stringScheme({ required: false }),
            action_message: stringScheme({ required: false }),
            action_escalated_list: stringScheme({ required: false }),
            action_data_collection: stringScheme({ required: false }),
            action_fields_name: booleanScheme({ required: false }),
            action_fields_email: booleanScheme({ required: false }),
            action_fields_phone_number: booleanScheme({ required: false }),
            action_fields_custom: booleanScheme({ required: false }),
            action_message_type: stringScheme({ required: false }),
            message_before: stringScheme({ required: false }),
            receiver_email_addresses: stringEmailScheme({ required: false, format: true }),
            message_after: stringScheme({ required: false }),
            message_close: stringScheme({ required: false }),
            action_fields_custom_array: yup.array(
              yup.object({
                name: stringScheme({ required: false }),
                description: stringScheme({ required: false }),
              }),
            ),
          }),
        ),
      }),
    ),
  });

  const submit = (data) => {
    const isValid = validateWorkflowActions(data, setError);

    if (!isValid) {
      return;
    }

    const action = data.action.map((action) => getWorkflowRequestAction(action));

    const newWorkflow: Record<string, any> = {
      enabled: workflow.enabled,
      type: workflow.type,
      action: isCustom ? action : action[0],
    };

    if (isCustom) {
      newWorkflow.name = data.name;
      newWorkflow.description = data.description;
      newWorkflow.trigger_type = data.trigger_type;
      newWorkflow.trigger_value = data.trigger_value;
    }

    onSubmit(newWorkflow);
    notification.success(
      `${isCustom ? 'Custom workflow ' : 'Escalation '} settings have been updated successfully!`,
    );
    close();
  };

  const close = () => {
    setOpen(false);
  };

  const onOpenChange = (isOpen) => {
    if (!isOpen) {
      reset();
    }

    setOpen(isOpen);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger
          disabled={disabled}
          asChild
          onClick={(e) => e.stopPropagation()}
          className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
        >
          <WorkflowsEditIcon />
        </DialogTrigger>
        <DialogContent closeButtonHidden className={'overflow-hidden'}>
          <DialogHeader>
            <DialogTitle>{isCustom ? 'Custom workflow' : 'Escalation'} settings</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="content">
              <form
                id="edit-custom-workflow-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit(submit)(e);
                }}
                className={'max-h-[60vh] overflow-auto pr-4'}
              >
                <div className={'flex flex-col h-full w-full gap-5'}>
                  {isCustom && (
                    <WorkflowNameAndDescriptionBlock control={control} isCustom={isCustom} />
                  )}
                  {isCustom && (
                    <WorkflowTriggersBlock
                      control={control}
                      setValue={setValue}
                      watch={watch}
                      isCustom={isCustom}
                    />
                  )}
                  <WorkflowActionsBlock
                    control={control}
                    watch={watch}
                    isCustom={isCustom}
                    trigger={trigger}
                  />
                </div>
              </form>
              <div className="flex justify-center mt-5">
                <div className="flex gap-2.5">
                  <Button
                    variant="outline"
                    type="button"
                    className="min-w-24"
                    onClick={() => onOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button form="edit-custom-workflow-form" className="min-w-24">
                    Update
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
